import {formatBillingRate, type RequirementBillingType} from './billing-types'

type BillingCheckRequirement = {
    requirementType: 'worker' | 'rental_asset'
    workerTypeId?: string | null
    rentalAssetTypeId?: string | null
    billingRate?: string | null
    billingType?: RequirementBillingType | null
}

type BillingCheckTypes = {
    workerTypes: Array<{id: string; name: string; isDriver: boolean}>
    rentalAssetTypes: Array<{id: string; name?: string; isDrivenTruck?: boolean}>
}

type BilledRate = {
    requirement: BillingCheckRequirement
    rate: number
    billingType: RequirementBillingType | null
}

function getBilledRate(requirement: BillingCheckRequirement): BilledRate | null {
    const text = requirement.billingRate?.trim()
    const rate = text ? Number(text) : NaN
    if (!Number.isFinite(rate)) {
        return null
    }

    return {requirement, rate, billingType: requirement.billingType ?? null}
}

function isDriverRequirement(
    requirement: BillingCheckRequirement,
    types: BillingCheckTypes,
): boolean {
    const type = types.workerTypes.find((workerType) => workerType.id === requirement.workerTypeId)
    return requirement.requirementType === 'worker' && type?.isDriver === true
}

function isTruckRequirement(
    requirement: BillingCheckRequirement,
    types: BillingCheckTypes,
): boolean {
    const type = types.rentalAssetTypes.find(
        (assetType) => assetType.id === requirement.rentalAssetTypeId,
    )
    return requirement.requirementType === 'rental_asset' && type?.isDrivenTruck === true
}

function formatTruckLabel(billed: BilledRate, types: BillingCheckTypes): string {
    const name = types.rentalAssetTypes.find(
        (assetType) => assetType.id === billed.requirement.rentalAssetTypeId,
    )?.name
    return `${name ?? 'truck'} ${formatBillingRate(billed.rate, billed.billingType)}`
}

/**
 * Build a warning when both a truck and a driver requirement carry a billing rate. With `added`,
 * only that requirement can trigger the warning.
 */
export function truckAndDriverBillingWarning(
    requirements: BillingCheckRequirement[],
    types: BillingCheckTypes,
    options: {added?: BillingCheckRequirement} = {},
): string | null {
    const trucks = requirements
        .filter((requirement) => isTruckRequirement(requirement, types))
        .map(getBilledRate)
        .filter((billed) => billed != null)
    const drivers = requirements
        .filter((requirement) => isDriverRequirement(requirement, types))
        .map(getBilledRate)
        .filter((billed) => billed != null)
    const added = options.added
    const addedTruck = added ? trucks.find((billed) => billed.requirement === added) : undefined
    const addedDriver = added ? drivers.find((billed) => billed.requirement === added) : undefined
    if (added && !addedTruck && !addedDriver) {
        return null
    }

    const truck = addedTruck ?? trucks[0]
    const driver = addedDriver ?? drivers[0]
    if (truck == null || driver == null) {
        return null
    }

    const truckRate = formatBillingRate(truck.rate, truck.billingType)
    const driverRate = formatBillingRate(driver.rate, driver.billingType)
    let warning = `Truck and driver are both billed (${formatTruckLabel(truck, types)}, driver ${driverRate}).`
    if (truckRate !== driverRate) {
        warning += ' Rates differ.'
    }
    const otherTrucks = trucks.filter((billed) => billed !== truck)
    if (otherTrucks.length > 0) {
        const others = otherTrucks.map((billed) => formatTruckLabel(billed, types)).join(', ')
        warning += ` Also billed: ${others}.`
    }
    return warning
}
